import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { fmtErr } from "@/lib/api";
import { Truck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(userId.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-blue-700 text-white">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-lg bg-white/10 backdrop-blur border border-white/20 grid place-items-center">
            <Truck className="h-5 w-5" />
          </div>
          <div className="font-heading font-bold text-lg">Dispatch Register</div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
            New Era Milk Foods<br />India Private Limited
          </h1>
          <p className="mt-4 text-blue-100/90 text-lg">
            Automated weight & freight calculations, admin-controlled edit approvals, complete audit trail, and instant transporter/customer reports.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { k: "Products", v: "SMP · Ghee" },
              { k: "Vehicle Types", v: "3 rulesets" },
              { k: "Reports", v: "PDF · XLSX" },
            ].map((s) => (
              <div key={s.k} className="rounded-xl bg-white/5 border border-white/10 p-3">
                <div className="text-[11px] uppercase tracking-wider text-blue-200">{s.k}</div>
                <div className="font-heading font-semibold mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-blue-200/70">© {new Date().getFullYear()} Dispatch Register</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardContent className="p-8">
            <div className="mb-6">
              <div className="lg:hidden flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-lg bg-blue-600 text-white grid place-items-center">
                  <Truck className="h-4 w-4" />
                </div>
                <div className="font-heading font-bold">Dispatch Register</div>
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
              <p className="text-sm text-slate-500 mt-1">Use your admin credentials to continue.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>USER ID</Label>
                <Input type="text" required value={userId} onChange={(e) => setUserId(e.target.value)}
                  data-testid="login-userid-input" placeholder="10-digit mobile number" autoComplete="username" inputMode="numeric" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input" placeholder="••••••••" />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={busy} data-testid="login-submit-btn">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
              <p className="text-xs text-slate-500 text-center pt-2">
                Forgot your password? Ask your admin to generate a one-time reset link from the Users screen.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
